import React, { useState, useEffect, useContext } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton, TextField, Button, MenuItem, 
  FormControlLabel, Switch, Autocomplete, Box, FormGroup } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Franchise, Store, Item } from '../../../app/services/api/generated';
import useNswagClient from '../../../hooks/api/useNswagClient';
import useLogError from '../../../hooks/useLogError';
import { MappingType } from '../enums/MappingType';
import { CupsAndLidsType } from '../enums/CupsAndLidsType';
import { PaperPackingType } from '../enums/PaperPackagingType';
import { availableCupSize } from '../enums/CupSize';
import { UserContext } from '../../../components/shared/useUser';
import { alseaLACFranchises } from '../../../enums/FranchiseNames';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { ItemType } from '../enums/ItemType';

const cupsAndLidsTypeLabelMap: Record<CupsAndLidsType, string> = {
  [CupsAndLidsType.Standard]: 'Standard',
  [CupsAndLidsType.StandardEcoCompostable]: 'Standard Eco Compostable',
  [CupsAndLidsType.Reserve]: 'Reserve',
  [CupsAndLidsType.ReserveEcoCompostable]: 'Reserve Eco Compostable',
};

const getMappingTypeOptions = () => [
  { label: 'POS', value: MappingType.POS },
  { label: 'Paper Packaging', value: MappingType.PaperPackaging },
];

const getItemTypeOptions = () => [
  { label: 'Standalone', value: 1 },
  { label: 'Milk', value: 2 },
  { label: 'BOM Milk', value: 3 },
];

const getCupsAndLidsTypeOptions = (isAlseaLACFranchise: boolean) => {
  const options = [
    { label: 'Standard', value: CupsAndLidsType.Standard },
  ];
  
  if (isAlseaLACFranchise) {
    options.push(
      { label: 'Standard Eco Compostable', value: CupsAndLidsType.StandardEcoCompostable },
      { label: 'Reserve', value: CupsAndLidsType.Reserve },
      { label: 'Reserve Eco Compostable', value: CupsAndLidsType.ReserveEcoCompostable },
    );
  }
  return options;
};

const getPaperPackagingTypeOptions = (isAlseaLACFranchise: boolean) => {
  const options = [
    { label: 'Hot Cup', value: PaperPackingType.HotCup },
    { label: 'Hot Lid', value: PaperPackingType.HotLid },
    { label: 'Cold Cup', value: PaperPackingType.ColdCup },
    { label: 'Cold Flat Lid', value: PaperPackingType.ColdFlatLid },
    { label: 'Cold Domed Lid', value: PaperPackingType.ColdDomedLid },
    { label: 'Sleeve', value: PaperPackingType.Sleeve },
    { label: 'Straw', value: PaperPackingType.Straw },
  ];
  
  if (isAlseaLACFranchise) {
    options.push({ label: 'Nitro Lid', value: PaperPackingType.NitroLid });
  }
  
  return options;
};

export interface ItemMappingFormData {
  itemNumber?: string;
  mappingType?: MappingType;
  itemType?: string;
  posItemNumber?: string;
  franchise?: string[];
  allStores?: boolean;
  stores?: string[];
  cupsAndLidsType?: string;
  paperPackagingType?: string;
  cupSize?: string[];
  // Original values for PUT requests
  originalPosItemNumber?: string;
  originalCupsAndLidsType?: string;
  originalPaperPackagingType?: string;
}

export interface ItemMappingModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: ItemMappingFormData) => void;
  editMode?: boolean;
  initialData?: ItemMappingFormData;
  setShowResultMessage?: (msg: { open: boolean; message: string; severity: 'error' | 'warning' | 'info' | 'success' }) => void;
  onTabSwitch?: (tabIndex: number) => void;
}

const ItemMappingModal: React.FC<ItemMappingModalProps> = ({ open, onClose, onSave, editMode = false, initialData, setShowResultMessage, onTabSwitch }) => {
  const { getFranchises, getListFranchiseStores, createPosMapping, createPaperPackagingMapping, updatePosMapping, updatePaperPackagingMapping, getItemsSearchedByStoreNumbers } = useNswagClient();
  const { logError } = useLogError();
  const { selectedStore } = useContext(UserContext);
  const isAlseaLACFranchise = alseaLACFranchises.includes(selectedStore?.franchiseName ?? '');
  
  const [itemNumber, setItemNumber] = useState(initialData?.itemNumber || '');
  const [mappingType, setMappingType] = useState(
    initialData?.mappingType,
  );
  const [itemType, setItemType] = useState<number>(ItemType.StandaloneItem); 
  const [posItemNumber, setPosItemNumber] = useState(initialData?.posItemNumber || '');
  const [franchise, setFranchise] = useState<Franchise[]>([]); 
  const [cupsAndLidsType, setCupsAndLidsType] = useState<number>(CupsAndLidsType.Standard);
  const [paperPackagingType, setPaperPackagingType] = useState<number>(PaperPackingType.HotCup);
  const [cupSize, setCupSize] = useState<number[]>([]);
  
  // State for franchise and store management
  const [franchiseList, setFranchiseList] = useState<Franchise[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [selectedStores, setSelectedStores] = useState<string[]>([]);
  const [checkedAllStores, setCheckedAllStores] = useState(false);

  // State for item search functionality
  const [searchTerm, setSearchTerm] = useState('');
  const [selectableItems, setSelectableItems] = useState<Item[]>([]);
  const [isItemsLoading, setIsItemsLoading] = useState<boolean>(false);
  const [itemDisplayValue, setItemDisplayValue] = useState<string | null>(null);

  // Validation state
  const [posSkuError, setPosSkuError] = useState<boolean>(false);

  const isPOS = mappingType === MappingType.POS;
  const isPaperPackaging = mappingType === MappingType.PaperPackaging;
  const { t } = useTranslation('posMapping');

  useEffect(() => {
    if (open) {
      setPosSkuError(false); 
      getFranchiseData();
      if (!editMode) {
        setItemNumber('');
        setMappingType(MappingType.POS);
        setItemType(ItemType.StandaloneItem); 
        setPosItemNumber('');
        setFranchise([]);
        setStores([]);
        setSelectedStores([]);
        setCheckedAllStores(false);
        setCupsAndLidsType(CupsAndLidsType.Standard);
        setPaperPackagingType(PaperPackingType.HotCup);
        setCupSize([]);
        // Reset search state
        setSearchTerm('');
        setSelectableItems([]);
        setItemDisplayValue(null);
      } else if (editMode && initialData) {
        setItemNumber(initialData.itemNumber || '');
        setSearchTerm(initialData.itemNumber || '');
        setItemDisplayValue(initialData.itemNumber || '');
        setMappingType(initialData.mappingType);
        setItemType(initialData.itemType ? parseInt(initialData.itemType) : 1);
        setPosItemNumber(initialData.posItemNumber || '');
        setCupsAndLidsType(initialData.cupsAndLidsType ? parseInt(initialData.cupsAndLidsType) : CupsAndLidsType.Standard);
        setPaperPackagingType(initialData.paperPackagingType ? parseInt(initialData.paperPackagingType) : PaperPackingType.HotCup);
        
        if (initialData.cupSize) {
          const cupSizeNumbers = initialData.cupSize
            .map(sizeLabel => availableCupSize.find(opt => opt.value === sizeLabel)?.key)
            .filter(key => key !== undefined) as number[];
          setCupSize(cupSizeNumbers);
        } else {
          setCupSize([]);
        }
      }
    }
  }, [open, editMode, initialData]);

  useEffect(() => {
    if (editMode && initialData && franchiseList.length > 0 && open) {
      const selectedFranchises = franchiseList.filter(f => 
        initialData.franchise?.includes(f.franchiseName || ''),
      );
      setFranchise(selectedFranchises);
      
      const allStoresSelected = !initialData.stores || initialData.stores.length === 0;
      setCheckedAllStores(allStoresSelected);
      
      // Load stores for the selected franchises
      if (selectedFranchises.length > 0) {
        const franchiseNames = selectedFranchises
          .map(f => f.franchiseName)
          .filter((name): name is string => !!name);
        
        getListFranchiseStores(franchiseNames).then((result) => {
          if (result?.data) { 
            setStores(result.data?.stores || []);

            if (allStoresSelected) {
              setSelectedStores([]);
            } else if (initialData.stores && initialData.stores.length > 0) {
              const storeNumbers = initialData.stores
                .map(storeIdentifier => {
                  const byNumber = result.data?.stores?.find(s => s.storeNumber === storeIdentifier);
                  if (byNumber) return byNumber.storeNumber;
                  
                  const byName = result.data?.stores?.find(s => s.storeName === storeIdentifier);
                  if (byName) return byName.storeNumber;
                  
                  const existsAsNumber = result.data?.stores?.some(s => s.storeNumber === storeIdentifier);
                  if (existsAsNumber) return storeIdentifier;
                  
                  return null;
                })
                .filter(Boolean) as string[];
              
              setSelectedStores(storeNumbers);
            }
          }
        }).catch(logError);
      }
    }
  }, [editMode, initialData, franchiseList, open]);

  const getFranchiseData = () => {
    getFranchises().then((result) => {
      if (result?.data) {
        setFranchiseList(result.data);
      }})
      .catch((error) => {
        logError(error);
      });
  };

  const getStoresByFranchiseNames = (franchiseNames: string[], allStoresSelected: boolean) => {
    getListFranchiseStores(franchiseNames).then((result) => {
      if (result?.data) { 
        setStores(result.data?.stores || []);

        if (allStoresSelected) {
          setSelectedStores(result.data?.stores?.map(store => store.storeNumber).filter(Boolean) as string[] || []);
          setCheckedAllStores(true);
        }
      }
    })
      .catch(logError);
  };

  const handleSearchTermChange = (value: string) => {
    setSearchTerm(value);
    if (value && selectedStores.length > 0) {
      setIsItemsLoading(true);
      setSelectableItems([]);
      getItemsSearchedByStoreNumbers({ stores: selectedStores, search: value })
        .then((result) => {
          setSelectableItems(result.data ?? []);
          setIsItemsLoading(false);
        })
        .catch((error) => {
          if (!axios.isCancel(error)) {
            logError(error);
            setIsItemsLoading(false);
          }
        });
    } else {
      setSelectableItems([]);
    }
  };

  const handleFranchiseChange = (
    event: React.SyntheticEvent,
    newValue: Franchise[],
  ) => {
    const franchiseNames = newValue
      .map((f) => f.franchiseName)
      .filter((name): name is string => !!name);
  
    setFranchise(newValue);
    setCheckedAllStores(false);
    setSelectedStores([]);
    
    if (!editMode) {
      setItemNumber('');
      setItemDisplayValue(null);
      setSearchTerm('');
      setSelectableItems([]);
    }
    
    if (franchiseNames.length > 0) {
      getStoresByFranchiseNames(franchiseNames, false);
    } else {
      setStores([]);
    }
  };

  const handleSelectAllChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    if (checked) {
      const newSelectedStores = stores?.map(store => store.storeNumber).filter(Boolean) as string[] || [];
      setSelectedStores(newSelectedStores);
    } else {
      setSelectedStores([]);
    }
    setCheckedAllStores(checked);
    if (!editMode) {
      setItemNumber('');
      setItemDisplayValue(null);
      setSearchTerm('');
      setSelectableItems([]);
    }
  };

  const handleStoreChange = (event: React.SyntheticEvent, newValue: Store[]) => {
    const selectedIds = newValue.map(store => store.storeNumber).filter(Boolean) as string[];
    setSelectedStores(selectedIds);
    if (!editMode) {
      setItemNumber('');
      setItemDisplayValue(null);
      setSearchTerm('');
      setSelectableItems([]);
    }
  };

  const handlePaperPackagingTypeChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = Number(event.target.value);
    setPaperPackagingType(value);
    
    // Clear cup sizes if Straw is selected
    if (value === PaperPackingType.Straw) {
      setCupSize([]);
      setCupsAndLidsType(CupsAndLidsType.Standard);
    }
  };

  const isSaveEnabled = () => {
    let valid = itemNumber && 
                mappingType !== undefined &&
                franchise.length > 0 &&
                (checkedAllStores || selectedStores.length > 0);
    
    if (isPOS) {
      valid = valid && posItemNumber.trim() !== '';
    }
    
    if (isPaperPackaging) {
      valid = valid && 
              cupsAndLidsType !== undefined &&
              paperPackagingType !== undefined &&
              (paperPackagingType === PaperPackingType.Straw ? 
                cupSize.length === 0 && cupsAndLidsType === CupsAndLidsType.Standard : 
                cupSize.length > 0);
    }
    
    return valid;
  };
  const handleSave = async () => {
    if (!isSaveEnabled()) return;
    setPosSkuError(false);
    
    try {
      const franchiseIds = franchise.map(f => f.franchiseId).filter(Boolean) as number[];
      
      if (isPOS) {
        if (editMode) {
          await updatePosMapping({
            newSource: posItemNumber, 
            source: initialData?.originalPosItemNumber || initialData?.posItemNumber || '', 
            target: itemNumber,
            mappingType: itemType,
            allStores: checkedAllStores,
            storeNumbers: checkedAllStores ? [] : selectedStores,
            franchiseIds: franchiseIds,
          });
        } else {
          await createPosMapping({
            source: posItemNumber,
            target: itemNumber,
            mappingType: itemType, 
            allStores: checkedAllStores,
            storeNumbers: checkedAllStores ? [] : selectedStores,
            franchiseIds: franchiseIds,
          });
        }
      } else if (isPaperPackaging) {
        if (editMode) {
          await updatePaperPackagingMapping({
            itemNumber: itemNumber,
            cupsLidsType: cupsAndLidsType,
            paperPackagingType: paperPackagingType,
            sizes: cupSize,
            allStores: checkedAllStores,
            storeNumbers: checkedAllStores ? [] : selectedStores,
            franchiseIds: franchiseIds,
            oldCupsLidsType: initialData?.originalCupsAndLidsType ? parseInt(initialData.originalCupsAndLidsType) : (initialData?.cupsAndLidsType ? parseInt(initialData.cupsAndLidsType) : undefined),
            oldPaperPackagingType: initialData?.originalPaperPackagingType ? parseInt(initialData.originalPaperPackagingType) : (initialData?.paperPackagingType ? parseInt(initialData.paperPackagingType) : undefined),
          });
        } else {
          await createPaperPackagingMapping({
            itemNumber: itemNumber,
            cupsLidsType: cupsAndLidsType,
            paperPackagingType: paperPackagingType,
            sizes: cupSize,
            allStores: checkedAllStores,
            storeNumbers: checkedAllStores ? [] : selectedStores,
            franchiseIds: franchiseIds,
          });
        }
      }
      onSave({
        itemNumber,
        mappingType: mappingType,
        itemType: itemType === 1 ? 'Standalone' : itemType === 2 ? 'Milk' : 'BOM Milk',
        posItemNumber,
        franchise: franchise.map(f => f.franchiseName).filter(Boolean) as string[],
        allStores: checkedAllStores,
        stores: checkedAllStores ? [] : selectedStores,
        cupsAndLidsType: cupsAndLidsTypeLabelMap[cupsAndLidsType as CupsAndLidsType] ?? 'Standard',
        paperPackagingType: getPaperPackagingTypeOptions(isAlseaLACFranchise).find(opt => opt.value === paperPackagingType)?.label || 'Hot Cup',
        cupSize: cupSize.map(size => availableCupSize.find(opt => opt.key === size)?.value || '').filter(Boolean),
      });
      
      if (!editMode && onTabSwitch && mappingType !== undefined) {
        onTabSwitch(mappingType);
      }
      
      onClose();
    } catch (error) {
      logError(error);
      const errorMessage = typeof error === 'string' ? error : 
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message || 
        (error as { message?: string })?.message || 
        'Unknown error';

      if (errorMessage.includes('the same SKU already exists') || errorMessage.includes('same mappings already exist')) {
        setPosSkuError(true);
        if (setShowResultMessage) {
          setShowResultMessage({
            open: true,
            message: errorMessage, 
            severity: 'error',
          });
        }
      
      } else {
        if (setShowResultMessage) {
          setShowResultMessage({
            open: true,
            message: editMode ? t('errors.updating') : t('errors.creating'),
            severity: 'error',
          });
        }
      }
    }
  };

  return (
    <Dialog open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth>
      <DialogTitle>
        {editMode ? t('editItemMapping') : t('createItemMapping')}
        <IconButton aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box display="flex"
          flexDirection="column"
          gap={3}>
          <Autocomplete
            disabled={editMode || selectedStores.length === 0}
            loading={isItemsLoading}
            options={selectableItems.map(i => `${i.itemNumber} - ${i.description}`)}
            value={itemDisplayValue}
            onChange={(event, newValue: string | null) => {
              setItemDisplayValue(newValue);
              
              if (newValue) {
                const item = selectableItems.find(i => `${i.itemNumber} - ${i.description}` === newValue);
                if (item) {
                  setItemNumber(item.itemNumber || '');
                }
              } else {
                setSearchTerm('');
                setItemNumber('');
              }
            }}
            inputValue={searchTerm}
            onInputChange={(event, newInputValue) => {
              setSearchTerm(newInputValue);
              handleSearchTermChange(newInputValue);
            }}
            filterOptions={(x) => x}
            renderInput={(params) => (
              <TextField {...params}
                label={t('itemNumber')}
                variant="outlined"
                fullWidth
                margin="normal"
                disabled={editMode || selectedStores.length === 0}
                helperText={!editMode && selectedStores.length === 0 ? t('pleaseSelectStoresFirst') : ''} />
            )}
          />
          <TextField
            select
            label={t('mappingType')}
            value={mappingType}
            onChange={e => setMappingType(Number(e.target.value))}
            disabled={editMode}
            fullWidth
            margin="normal"
          >
            {getMappingTypeOptions().map(opt => (
              <MenuItem key={opt.value}
                value={opt.value}>{opt.label}</MenuItem>
            ))}
          </TextField>
          {isPOS && (
            <>
              <TextField
                select
                label={t('itemType')}
                value={itemType}
                onChange={e => setItemType(Number(e.target.value))}
                fullWidth
                margin="normal"
              >
                {getItemTypeOptions().map(opt => (
                  <MenuItem key={opt.value}
                    value={opt.value}>{opt.label}</MenuItem>
                ))}
              </TextField>
              <TextField
                label={t('posItemNumber')}
                value={posItemNumber}
                onChange={e => {
                  setPosItemNumber(e.target.value);
                  if (posSkuError) {
                    setPosSkuError(false);
                  }
                }}
                fullWidth
                margin="normal"
                error={posSkuError}
                helperText={posSkuError ? t('pleaseEnterValidSKU') : ''}
              />
            </>
          )}
          {isPaperPackaging && (
            <>
              <TextField
                select
                label={t('cups')}
                value={cupsAndLidsType}
                onChange={e => setCupsAndLidsType(Number(e.target.value))}
                fullWidth
                margin="normal"
              >
                {getCupsAndLidsTypeOptions(isAlseaLACFranchise).map(opt => (
                  <MenuItem key={opt.value}
                    value={opt.value}>{opt.label}</MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label={t('paperPackagingType')}
                value={paperPackagingType}
                onChange={handlePaperPackagingTypeChange}
                fullWidth
                margin="normal"
              >
                {getPaperPackagingTypeOptions(isAlseaLACFranchise).map(opt => (
                  <MenuItem key={opt.value}
                    value={opt.value}>{opt.label}</MenuItem>
                ))}
              </TextField>
              <Autocomplete
                multiple
                disabled={paperPackagingType === PaperPackingType.Straw}
                options={availableCupSize}
                value={availableCupSize.filter(opt => cupSize.includes(opt.key))}
                onChange={(_, v) => setCupSize(v.map(option => option.key))}
                getOptionLabel={(option) => option.value}
                renderInput={(params) => (
                  <TextField {...params}
                    label={t('cupSize')}
                    variant="outlined"
                    fullWidth
                    margin="normal" 
                    required={cupSize.length === 0 && isPaperPackaging && paperPackagingType !== PaperPackingType.Straw} />
                )}
              />
            </>
          )}
          <Autocomplete
            multiple
            options={franchiseList || []}
            value={franchise}
            onChange={handleFranchiseChange}
            getOptionLabel={(option) => option.franchiseName ?? ''}
            renderInput={(params) => (
              <TextField {...params}
                label={t('selectfranchise')}
                variant="outlined"
                fullWidth
                margin="normal" />
            )}
          />
          <FormGroup style={{ textAlign: 'right' }}>
            <FormControlLabel
              control={
                <Switch
                  disabled={stores?.length === 0}
                  checked={checkedAllStores}
                  onChange={handleSelectAllChange}
                />
              }
              label={t('allStores')}
            />
          </FormGroup>
          <Autocomplete
            multiple
            options={stores || []}
            value={stores?.filter(store => selectedStores?.includes(store.storeNumber ?? '')) ?? []}
            onChange={handleStoreChange}
            getOptionLabel={(option) => option.storeName ?? ''}
            disabled={checkedAllStores || franchise.length === 0}
            renderInput={(params) => (
              <TextField {...params}
                label={t('selectStores')}
                variant="outlined"
                fullWidth
                margin="normal" />
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}
          variant="secondary">{t('cancel')}</Button>
        <Button onClick={handleSave}
          variant="primary"
          disabled={!isSaveEnabled()}>{t('save')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ItemMappingModal;
